import { ProductPageData } from "../_types";

export const JSON_PRODUCT_MAJIK_UNIVERSAL_ID: ProductPageData = {
  id: "majik-universal-id",
  timestamp: new Date("2026-03-29").toISOString(),
  lastUpdate: new Date("2026-03-29").toISOString(),
  version: "1.0.0",
  isEnabled: true,
  repository: {
    url: "https://github.com/Majikah/majik-universal-id",
    owner: "Majikah",
    repo: "majik-universal-id",
  },
  metadata: {
    productName: "Majik Universal ID",
    productSlug: "majik-universal-id",
    tagline: "Your Cryptographic Identity for the Majikah Ecosystem",
    description:
      "A single, verified identity that ties your Majikah account to a cryptographic key and an optional real-world identity check. Sign files, prove URL ownership, and let anyone verify your content — all from one permanent, tamper-proof profile.",
    seo: "Digital identity, cryptographic identity, KYC verification, post-quantum identity, tamper-proof profile, content signing, URL ownership, Majikah ecosystem, verified identity",
    keywords: [
      "identity",
      "universal-id",
      "post-quantum",
      "kyc",
      "verification",
      "signing",
      "didit",
      "majikah",
      "profile",
      "content-provenance",
      "url-ownership",
      "slink",
    ],
    category: "Security",
    subcategory: "Identity & Verification",
    photo: {
      thumbnail:
        "https://storage.majikah.solutions/public/Logo_MajikUniversalID_Majikah_Thumbnail_WEBP.webp?url",
      banner:
        "https://storage.majikah.solutions/public/MajikUniversalID_SmallTile.jpg?url",
      screenshots: [],
      seo: "https://storage.majikah.solutions/public/Majikah_MajikUniversalID_SocialCard.webp?url",
    },
    video: [],
  },
  hero: {
    title: "Majik Universal ID",
    subtitle: "Your Cryptographic Identity for the Majikah Ecosystem",
    description:
      "One identity. One key. Fully yours. Majik Universal ID is your permanent public profile in the Majikah ecosystem — it ties your account to a signing key and an optional verified identity so anyone can confirm your signatures are genuine, without you sharing a single private detail.",
    ctaButtons: [
      {
        label: "Create Your ID",
        url: "https://id.majikah.solutions",
        variant: "primary",
      },
      {
        label: "View on npm",
        url: "https://www.npmjs.com/package/@majikah/majik-universal-id",
        variant: "primary",
        external: true,
      },
      {
        label: "View on GitHub",
        url: "https://github.com/Majikah/majik-universal-id",
        variant: "secondary",
        external: true,
      },
      {
        label: "See Technical Details",
        url: "#technical-specs",
        variant: "tertiary",
      },
      {
        label: "Read Docs",
        url: "/products/majik-universal-id/docs",
        variant: "tertiary",
      },
    ],
  },
  overview: {
    title: "What is a Majik Universal ID?",
    paragraphs: [
      "A Majik Universal ID (MUID) is your permanent, cryptographic identity in the Majikah ecosystem. Think of it as a public ID card that anyone can look up — showing your verification status, your signing keys, and the URLs and files you have signed — without ever revealing your private information.",
      "When you create a MUID, it is permanently linked to one Majik Key account. Everything you sign with that key — a file, a document, a public URL — can be traced back to your identity by anyone who visits your public profile. No registry, no third-party lookup required.",
      "Your private details — legal name, date of birth, phone number, address — are always encrypted and stored locally. Nobody sees them unless you choose to share. The public profile only shows what you have chosen to make visible: your display name, verification tier, and signed content.",
      "Verification is optional but recommended. Completing an identity check through Didit gives your profile a verified badge and adds a real-world trust layer to everything you sign. Until then, your MUID is still fully functional — you can sign files, publish signed links, and share your public profile right away.",
    ],
    highlights: [
      {
        icon: "fingerprint",
        label: "One Identity, One Key",
        description:
          "Your MUID is permanently linked to a single Majik Key. Any file or URL signed with that key is verifiable through your public profile — forever.",
      },
      {
        icon: "lock",
        label: "Your Private Info Stays Private",
        description:
          "Personal details are encrypted end-to-end using post-quantum ML-KEM-768. Nobody can read them — not even Majikah. Only you can decrypt them with your key.",
      },
      {
        icon: "shield",
        label: "Optional Identity Verification",
        description:
          "Complete a quick KYC check through Didit to earn a verified badge. Shows others that your identity has been cross-checked against a real government-issued ID.",
      },
      {
        icon: "checkCircle",
        label: "Public Profile Anyone Can Check",
        description:
          "Share your MUID link and let anyone drag a file onto your profile to instantly verify it was signed by you — no account needed on their end.",
      },
      {
        icon: "link",
        label: "Prove You Own Any URL",
        description:
          "Publish Majik SLinks to cryptographically prove you control a YouTube channel, social profile, website, or any public page — all tied to your verified identity.",
      },
      {
        icon: "ban",
        label: "Tamper-Proof Once Verified",
        description:
          "Once your MUID is verified, it becomes permanently anchored. No one — not even you — can alter the core identity record without leaving a detectable trace.",
      },
    ],
  },
  features: [
    {
      id: "public-profile",
      title: "Your Public Identity Profile",
      description:
        "Every Majik Universal ID comes with a permanent public profile page that anyone can visit — no login required. It shows your verification status, signing keys, and published signed content.",
      icon: "identificationCard",
      content: [
        "Your profile is accessible at id.majikah.solutions/id/{username} or id.majikah.solutions/id/{id}. Share the link anywhere — email, social bios, documents — so others can verify your identity.",
        "The profile shows your display name, verification tier (Unverified, Verified, Trusted, etc.), the public keys of your bound Majik Key, and all published SLinks.",
        "None of your private information — email, phone, legal name, address, date of birth — is ever visible on the public profile.",
        "Anyone can drag and drop a signed file onto your profile's verification zone to instantly confirm it was signed by your key. No account, no software, no technical knowledge needed.",
        "Your most recently published signed URLs (Majik SLinks) are listed on your profile, so anyone can see and verify the online resources you have claimed ownership of.",
      ],
    },
    {
      id: "one-key-binding",
      title: "Permanently Bound to One Majik Key",
      description:
        "Your MUID is tied to a single Majik Key account from the moment it is created. This creates a permanent, cryptographic link between your identity and every piece of content you sign.",
      icon: "key",
      content: [
        "When you create your MUID, you load your Majik Key backup file — your 12/24-word seed phrase identity — to bind it. This connection is permanent.",
        "Every file, document, or URL you sign with that key can be publicly verified as coming from you — no one else can produce signatures that verify against your profile.",
        "Your Majik Key is interoperable across all Majikah products. The same key you use in Majik Signature or Majik Message is the one that anchors your MUID.",
        "If you ever want to use a different key, you can delete and recreate your MUID while it is unverified. Once verified, the key binding is permanent for 30 days.",
        "The MUID cryptographically commits to your key at creation time — any attempt to substitute the key material is detectable.",
      ],
    },
    {
      id: "private-info-encryption",
      title: "Private Info — Encrypted, Always",
      description:
        "Your personal details are encrypted with your key the moment your MUID is created. They live in encrypted form on the server and only you can read them.",
      icon: "lockKey",
      content: [
        "Legal name, date of birth, gender, email, phone number, and home address are all encrypted using ML-KEM-768 — a NIST-standardized post-quantum encryption algorithm — before anything is saved.",
        "Nobody can read your private info without your Majik Key: not other users, not Majikah, not anyone. The encrypted data is useless without the key that encrypted it.",
        "To view your own private info in the panel, click Decrypt and load your Majik Key backup file. Decryption happens locally — your file is never uploaded anywhere.",
        "You can also securely share your private info with another person using Majik Envelope group encryption — each recipient gets an individually encrypted copy they can only open with their own key.",
        "Even after decrypting, the private info only exists in memory during that session. Closing or refreshing the page clears it completely.",
      ],
    },
    {
      id: "identity-verification",
      title: "Identity Verification — Earn Your Verified Badge",
      description:
        "Complete a quick identity check through Didit to earn a verification badge on your profile. This adds a real-world trust layer to everything you sign.",
      icon: "shieldCheck",
      content: [
        "Verification is powered by Didit, a trusted third-party KYC provider. The process is done in a separate Didit portal — Majikah only receives the result.",
        "The check has four stages: ID document scan, liveness check (a short selfie), face match against your document, and an IP risk check. All four must pass for a Verified badge.",
        "Having a verified MUID does not change what is public — your private info stays encrypted. It simply adds a badge that tells others your identity has been cross-checked against a real government ID.",
        "You can start verification anytime from your MUID panel. Once started, you can pause and resume the Didit session without losing progress.",
        "Once verified, your MUID is locked for 30 days. This is a security measure to prevent account tampering. After the lockout expires, you can revoke and recreate the identity if needed.",
      ],
      table: {
        headers: ["Verification Stage", "What It Checks", "Required For"],
        rows: [
          {
            cells: [
              "ID Verification",
              "Government-issued ID document",
              "Verified tier",
            ],
          },
          {
            cells: [
              "Liveness Check",
              "Real person, not a photo or video",
              "Enhanced tier",
            ],
          },
          {
            cells: [
              "Face Match",
              "Your selfie matches your ID photo",
              "Enhanced tier",
            ],
          },
          { cells: ["IP Analysis", "Network risk assessment", "Trusted tier"] },
        ],
      },
    },
    {
      id: "file-signing-verification",
      title: "Sign Files and Let Anyone Verify Them",
      description:
        "Use your bound Majik Key to sign any file or document. Anyone with your MUID profile link can verify it instantly — no account, no software, no technical knowledge needed.",
      icon: "pen",
      content: [
        "Sign PDFs, images, audio, video, and documents using Majik Signature or Majik Message with the same key bound to your MUID. The signature embeds directly into the file — no sidecar files.",
        "Share the signed file and your MUID profile link together. Recipients visit your profile, drop the file onto the verification zone, and instantly see whether it is genuine.",
        "The verification check confirms that both the classical (Ed25519) and post-quantum (ML-DSA-87) signatures are valid and that the file has not been altered since you signed it.",
        "Even a single character change to the file — intentional or accidental — invalidates the signature immediately.",
        "Signatures from your key are quantum-safe today. Even as computing technology advances, your content provenance remains protected.",
      ],
    },
    {
      id: "slinks",
      title: "Majik SLinks — Prove You Own Any URL",
      description:
        "Publish a Majik SLink to cryptographically prove you control a YouTube channel, social media profile, website, or any public page — all tied to your MUID.",
      icon: "linkSimple",
      content: [
        "A SLink is a signed ownership claim for a public URL. You embed a short verification code on your page, the Majikah scraper confirms it is there, and your key signs the URL — creating a tamper-proof proof of ownership.",
        "All your published SLinks appear on your public MUID profile page. Anyone can see what online resources you have claimed and verify each one with a single click.",
        "SLinks work for any public URL: YouTube channels, X/Twitter profiles, Instagram bios, GitHub accounts, personal websites, blogs, and more.",
        "The verification code is deterministic — the same URL always produces the same code, so you only need to embed it once. You can remove it after publishing the SLink.",
        "SLinks can be deleted anytime. Deletion removes the record from your public profile immediately.",
      ],
    },
  ],
  technicalSpecs: {
    title: "Technical Specifications",
    subsections: [
      {
        title: "Cryptographic Stack",
        description:
          "Majik Universal ID uses NIST-standardized post-quantum algorithms alongside classical cryptography for identity anchoring and private data encryption.",
        table: {
          headers: ["Component", "Algorithm", "Standard", "Usage"],
          rows: [
            {
              cells: [
                "Identity Integrity Hash",
                "SHA3-512",
                "NIST FIPS-202",
                "Commits all four bound public keys at creation",
              ],
            },
            {
              cells: [
                "Identity Anchor Signature (classical)",
                "Ed25519",
                "RFC 8032",
                "Signs core identity fields at creation",
              ],
            },
            {
              cells: [
                "Identity Anchor Signature (post-quantum)",
                "ML-DSA-87",
                "NIST FIPS-204",
                "Post-quantum co-signature over identity fields",
              ],
            },
            {
              cells: [
                "Private Info Encryption",
                "ML-KEM-768",
                "NIST FIPS-203",
                "Encrypts personal info at rest — decryptable only with bound key",
              ],
            },
            {
              cells: [
                "Content Signing (classical)",
                "Ed25519",
                "RFC 8032",
                "Signs files and text via Majik Signature",
              ],
            },
            {
              cells: [
                "Content Signing (post-quantum)",
                "ML-DSA-87",
                "NIST FIPS-204",
                "Post-quantum co-signature on all signed content",
              ],
            },
          ],
        },
      },
      {
        title: "Verification Tiers",
        description:
          "IDTier reflects how much verification has been completed. It increases as you complete more Didit stages and only decreases if you explicitly revoke verification.",
        table: {
          headers: ["Tier", "Stages Completed", "What It Means", "Mutable?"],
          rows: [
            {
              cells: [
                "UNVERIFIED",
                "None",
                "Account created, no identity check completed. Can be changed anytime.",
                "Yes",
              ],
            },
            {
              cells: [
                "BASIC",
                "Phone verification only",
                "Phone number confirmed via OTP.",
                "No",
              ],
            },
            {
              cells: [
                "VERIFIED",
                "ID verification",
                "Government ID document confirmed.",
                "No",
              ],
            },
            {
              cells: [
                "ENHANCED",
                "ID + Liveness + Face Match",
                "Document, selfie, and face match all passed.",
                "No",
              ],
            },
            {
              cells: [
                "TRUSTED",
                "All 4 stages",
                "Full verification complete. Highest trust tier.",
                "No",
              ],
            },
          ],
        },
      },
      {
        title: "Platform Support",
        description:
          "Majik Universal ID works in the browser and as an npm package for developers.",
        table: {
          headers: ["Platform", "Status", "Notes"],
          rows: [
            {
              cells: [
                "ID Web App",
                "✓ Available",
                "id.majikah.solutions — create and manage your MUID in the browser",
              ],
            },
            {
              cells: [
                "Modern browsers",
                "✓ Supported",
                "Chrome, Firefox, Safari, Edge",
              ],
            },
            {
              cells: [
                "Node.js 18+",
                "✓ Supported",
                "Available as @majikah/majik-universal-id on npm",
              ],
            },
            {
              cells: [
                "Deno / Bun",
                "✓ Supported",
                "No native bindings required",
              ],
            },
          ],
        },
      },
      {
        title: "Error Reference",
        description: "Common errors you may encounter, and what they mean.",
        table: {
          headers: ["Error", "When It Happens"],
          rows: [
            {
              cells: [
                "Identity not mutable",
                "You tried to change something on a verified identity. Wait for the 30-day lock to expire or create a new MUID after revoking.",
              ],
            },
            {
              cells: [
                "Verification locked",
                "You tried to revoke verification within 30 days of completing it. Check your panel for the exact days remaining.",
              ],
            },
            {
              cells: [
                "Private info locked",
                "You tried to view private info before decrypting. Click Decrypt and load your key backup file.",
              ],
            },
            {
              cells: [
                "Key fingerprint mismatch",
                "The Majik Key file you loaded does not match the key bound to this MUID. Use the correct backup file.",
              ],
            },
            {
              cells: [
                "Integrity hash mismatch",
                "The identity record may have been corrupted or tampered with. Contact support if this appears unexpectedly.",
              ],
            },
          ],
        },
      },
    ],
  },
  howItWorks: {
    title: "How Majik Universal ID Works",
    processes: [
      {
        title: "Creating Your MUID — 3 Steps",
        description:
          "Setting up your Majik Universal ID takes about five minutes and requires a Majikah account and a Majik Key backup file.",
        steps: [
          {
            text: "Confirm your profile. Review the personal information pulled from your Majikah account — legal name, date of birth, address. Edit anything that needs correcting. This information will be encrypted and used during Didit KYC if you choose to verify, so it must match your government ID exactly.",
            type: "numbered",
          },
          {
            text: "Load your Majik Key. Drag your Majik Key backup (.json) file onto the drop zone. The key is loaded into memory only — it is never uploaded. Once loaded, your key's fingerprint and public keys are bound to your MUID permanently.",
            type: "numbered",
          },
          {
            text: "Create. Click Create Universal ID. Your private info is encrypted with your key, the identity record is signed, and your MUID is registered. You are taken straight to your new profile — ready to sign files, publish SLinks, or start verification.",
            type: "numbered",
          },
        ],
        note: "If you need to correct your information after creating an unverified MUID, click Delete & Re-create. The setup wizard reopens immediately so you can start fresh.",
      },
      {
        title: "Getting Verified",
        description:
          "Verification is optional, but adds a badge to your profile and increases trust for anyone checking your signatures.",
        steps: [
          {
            text: "From your MUID panel, click Start Verification. A Didit session is created and a new tab opens with the Didit portal.",
            type: "numbered",
          },
          {
            text: "Complete the Didit KYC flow: scan your government ID, complete a liveness check, and allow the face match. This typically takes 3–10 minutes.",
            type: "numbered",
          },
          {
            text: "Return to id.majikah.solutions/id and click Refresh. If all stages passed, your MUID now shows a Verified (or higher) badge.",
            type: "numbered",
          },
          {
            text: "If a stage is under review, the panel shows In Review status. Manual reviews can take a few hours. Check back later and refresh.",
            type: "numbered",
          },
        ],
        note: "Your private info is never shared with Didit directly. Majikah only receives the pass/fail result from each stage.",
      },
      {
        title: "Decrypting Your Private Info",
        description:
          "Your personal details are always encrypted. Here is how to view them in your session.",
        steps: [
          {
            text: "In your MUID panel, click Decrypt or the unlock icon next to Private Info.",
            type: "numbered",
          },
          {
            text: "Drag your Majik Key backup (.json) file onto the dialog, or click Browse to find it on your device.",
            type: "numbered",
          },
          {
            text: "Once loaded, click Decrypt. Your private info appears immediately — name, DOB, phone, address, and more.",
            type: "numbered",
          },
          {
            text: "Decrypted data exists only in memory for this session. It disappears when you close or refresh the page — and is never stored in decrypted form.",
            type: "numbered",
          },
        ],
        note: "If the decryption fails, make sure you are loading the correct key backup file — the one that was bound to this specific MUID.",
      },
      {
        title: "Verifying Someone Else's File",
        description:
          "Anyone can verify a file against a MUID profile — no account or software required.",
        steps: [
          {
            text: "Visit the signer's public profile: id.majikah.solutions/id/{username} or id.majikah.solutions/id/{id}",
            type: "numbered",
          },
          {
            text: "Find the Verify a Signature section on the profile page.",
            type: "numbered",
          },
          {
            text: "Drag the signed file onto the drop zone, or click to browse. The check runs entirely in your browser — the file is never uploaded.",
            type: "numbered",
          },
          {
            text: "Read the result. Green means the signature is valid and the file has not been modified. Red means verification failed — the file may have been tampered with or was not signed by this identity.",
            type: "numbered",
          },
        ],
        note: "The verification checks both the classical (Ed25519) and post-quantum (ML-DSA-87) signatures. Both must pass for the result to be valid.",
      },
    ],
  },
  pricing: {
    title: "Pricing",
    model: "freemium",
    currentStatus:
      "Majik Universal ID is completely **free** for individual use. Creating a MUID, completing verification, signing files, and publishing SLinks all cost nothing.",
    futureStatus:
      "**Coming soon:** Enterprise features for organizations managing multiple identities may be introduced in the future. Personal use will always remain free.",
  },
  security: {
    title: "Security — What You Need to Know",
    categories: [
      {
        title: "What Majik Universal ID Protects",
        items: [
          {
            label: "Your private info:",
            description:
              "Encrypted with ML-KEM-768 at creation. Never stored in plaintext. Nobody — including Majikah — can read it without your key.",
          },
          {
            label: "Your signed content:",
            description:
              "Any byte change to a file you signed invalidates both signatures immediately. Tampering is cryptographically detectable.",
          },
          {
            label: "Your identity record:",
            description:
              "A tamper-evident integrity hash covers your key material at creation. Any substitution of key fields is detectable on load.",
          },
          {
            label: "Verification results:",
            description:
              "Didit webhook results are HMAC-verified before processing — forged webhook payloads are rejected.",
          },
          {
            label: "Quantum-safe signatures:",
            description:
              "ML-DSA-87 protects your signatures against quantum computer attacks. Your content provenance is safe even as computing advances.",
          },
        ],
      },
      {
        title: "Your Responsibilities",
        items: [
          {
            label: "Keep your Majik Key backup safe:",
            description:
              "Your .json backup file and 12/24-word seed phrase are the only way to recover your key. Anyone who has them can sign as you. Store them securely offline.",
          },
          {
            label: "Lock your key after use:",
            description:
              "After signing or decrypting, your key should be locked. Never leave an unlocked key session open on a shared or untrusted device.",
          },
          {
            label: "Verify your profile information:",
            description:
              "If you plan to complete KYC verification, your profile details must match your government ID exactly — incorrect info will cause Didit to reject the submission.",
          },
          {
            label: "Do not delete verified identities casually:",
            description:
              "A verified MUID is locked for 30 days. Plan ahead if you need to make changes.",
          },
        ],
      },
      {
        title: "Good to Know",
        items: [
          {
            label: "Only one MUID per account:",
            description:
              "Each Majikah account can have one MUID at a time, and each Majik Key can only be bound to one MUID.",
          },
          {
            label: "Unverified MUIDs can be recreated freely:",
            description:
              "If you have not completed KYC, you can delete and recreate your MUID as many times as needed to correct your info or swap your key.",
          },
          {
            label: "Verification is not required:",
            description:
              "Your MUID is fully functional without verification — you can sign files and publish SLinks immediately after creation.",
          },
          {
            label: "The public profile shows nothing private:",
            description:
              "Only your display name, verification tier, public keys, and SLinks are ever visible to others.",
          },
        ],
      },
    ],
  },
  useCases: [
    {
      title: "Prove Your Files Are Genuine",
      description:
        "Sign any file — a contract, a photo, a report, a piece of creative work — and share your MUID profile link alongside it. Anyone can drag the file onto your profile and instantly confirm it has not been altered and came from you. No account needed on their end.",
    },
    {
      title: "Prove You Own Your Online Presence",
      description:
        "Publish Majik SLinks for your YouTube channel, social media profiles, GitHub, or personal website. Each SLink is cryptographically signed and publicly verifiable — giving anyone a trustless way to confirm your online accounts and content really belong to you.",
    },
    {
      title: "Build a Trusted Public Profile",
      description:
        "Your public MUID page shows your verification tier, signing keys, and all your published signed content in one place. Share it as your cryptographic identity card — in your email signature, bio, or professional profiles.",
    },
    {
      title: "Secure Sensitive Announcements",
      description:
        "Publish important statements, legal notices, or sensitive documents as signed content. Anyone who receives them can verify they came from your verified identity and have not been modified since you published them.",
    },
    {
      title: "Identity for the Majikah Ecosystem",
      description:
        "Your MUID is the shared identity layer across all Majikah products. The same key that signs your files in Majik Signature secures your messages in Majik Message and anchors your SLinks — all from one unified identity.",
    },
    {
      title: "Long-Term Content Provenance",
      description:
        "Your signatures use post-quantum cryptography that will remain secure for decades. Sign a dataset, a research paper, or a legal document today and it will still be verifiably yours long after classical cryptography becomes obsolete.",
    },
    {
      title: "Journalists and Investigators",
      description:
        "Sign raw footage, photos, or documents at the point of capture to prove they are authentic originals. Anyone — editors, courts, or the public — can verify the files came from your identity and have not been edited.",
    },
    {
      title: "Creators and Artists",
      description:
        "Sign your work — music, art, video, writing — to establish cryptographic authorship. Buyers, galleries, and fans can verify provenance from your public MUID profile at any time, with no middleman.",
    },
  ],
  gettingStarted: {
    title: "Getting Started",
    steps: [
      {
        title: "1. Create a Majikah Account",
        items: [
          "Go to majikah.solutions and sign up for a free account.",
          "Fill in your profile with accurate personal information — this gets encrypted into your MUID.",
          "If you already have an account, skip to the next step.",
        ],
      },
      {
        title: "2. Get a Majik Key",
        items: [
          "You need a Majik Key backup (.json) file to create your MUID.",
          "If you already use Majik Signature or Majik Message, you can reuse your existing key — all Majik Keys work across all Majikah products.",
          "To create a new key, open signature.majikah.solutions, go to the Accounts tab, click the + icon, generate a new 12/24-word seed phrase, and export the backup file.",
          "Store your backup file and seed phrase somewhere safe. This is the only way to recover your key.",
        ],
      },
      {
        title: "3. Create Your MUID",
        items: [
          "Go to id.majikah.solutions/id and log in.",
          "The setup wizard launches automatically. Review your profile info and click Confirm & Continue.",
          "Drag your Majik Key .json backup onto the drop zone and click Create Universal ID.",
          "Your MUID is created immediately. You can start using it right away.",
        ],
      },
      {
        title: "4. (Optional) Get Verified",
        items: [
          "From your MUID panel, click Start Verification.",
          "Complete the Didit KYC flow: scan your ID, do a liveness check, and pass the face match.",
          "Return to your panel and click Refresh to see your updated verification badge.",
        ],
      },
      {
        title: "5. Share Your Profile",
        items: [
          "Click the Share button (↗) in your MUID panel toolbar to copy your public profile link.",
          "Your link is id.majikah.solutions/id/{username} if you have a username set, or id.majikah.solutions/id/{id} otherwise.",
          "Share it in your email signature, social bios, or anywhere you want people to be able to verify your identity.",
        ],
      },
      {
        title: "6. Sign Your First File",
        items: [
          "Open Majik Signature at signature.majikah.solutions.",
          "Load the same Majik Key that is bound to your MUID.",
          "Drop any file onto the Sign tab and click Sign.",
          "Download the signed file and share it alongside your MUID profile link so anyone can verify it.",
        ],
      },
      {
        title: "7. Publish Your First SLink",
        items: [
          "Go to id.majikah.solutions/slink.",
          "Enter a public URL you control (YouTube, social profile, website, etc.).",
          "Copy the challenge code and paste it somewhere on that page.",
          "Return and click Verify, then Publish. Your SLink is now live on your public MUID profile.",
        ],
      },
    ],
  },
  roadmap: {
    title: "What's Coming Next",
    description:
      "Majik Universal ID is actively developed. Here is what is on the roadmap:",
    items: [
      {
        label: "Richer ID panel",
        description:
          "More detailed views for verification stages, consent history, and private info sharing — all from the web app.",
        status: "in-progress",
      },
      {
        label: "Username customization",
        description:
          "Set a custom username for your public profile URL so it is easier to share.",
        status: "coming-soon",
      },
      {
        label: "Didit session management",
        description:
          "Better in-app controls for starting, pausing, resuming, and checking the status of your Didit verification session.",
        status: "coming-soon",
      },
      {
        label: "Private info sharing UI",
        description:
          "Share encrypted private info with specific people directly from the web app — no code required.",
        status: "planned",
      },
      {
        label: "Consent management dashboard",
        description:
          "View and revoke data-sharing consents from a dedicated dashboard in your MUID panel.",
        status: "planned",
      },
      {
        label: "Multi-tenant organization support",
        description:
          "Manage multiple MUIDs across a team or organization with shared administration tools.",
        status: "planned",
      },
      {
        label: "Mobile app",
        description:
          "Manage your MUID, verify files, and publish SLinks from iOS and Android.",
        status: "planned",
      },
      {
        label: "Audit trail export",
        description:
          "Export a full history of your signing operations, verification events, and consent changes for compliance or personal records.",
        status: "planned",
      },
    ],
  },
  ecosystem: {
    title: "Part of the Majikah Ecosystem",
    paragraphs: [
      "Majik Universal ID is the identity layer connecting all Majikah products. Your MUID brings together your Majik Key, your verified identity, and your signed content into one permanent, publicly verifiable profile.",
      "Every Majik Key you create in Majik Signature or Majik Message is compatible with Majik Universal ID. One key, one identity, across all products. Sign a file in Majik Signature and anyone can verify it from your MUID profile. Publish a SLink from the ID app and it is signed with the same key that secures your messages.",
      "Majik Signature is the signing engine that powers all file and content signing through your MUID. Every time you sign a file, both Ed25519 and ML-DSA-87 signatures are produced — giving your content quantum-safe protection today.",
      "Majik Envelope handles the encryption of your private personal info. The same group encryption technology used in Majik Message ensures that your private details can only be read by you — and optionally by people you explicitly share them with.",
    ],
  },
  cta: {
    title: "Create Your Cryptographic Identity",
    description:
      "Your Majik Universal ID is free, permanent, and yours. Create it in under five minutes:",
    downloads: [
      { platform: "ID Web App", url: "https://id.majikah.solutions" },
      {
        platform: "npm (@majikah/majik-universal-id)",
        url: "https://www.npmjs.com/package/@majikah/majik-universal-id",
      },
      {
        platform: "GitHub",
        url: "https://github.com/Majikah/majik-universal-id",
      },
    ],
    closingStatement: "One identity. One key. Verifiable by anyone, anywhere.",
  },
  platforms: [{ id: "webapp", url: "https://id.majikah.solutions" }],
};
